import React, { useState, useEffect } from 'react';
import {
  Upload,
  Card,
  Select,
  Button,
  message,
  Table,
  Row,
  Col,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Empty,
  Tooltip,
  Input,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  TrophyOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import apiClient, { handleApiError } from '../utils/axiosConfig';

const { Option } = Select;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// ✅ Soft color palette
const certificateOptions = [
  {
    label: 'IATF 16949',
    value: 'IATF 16949',
    color: '#5B8DEF',
    description: 'Automotive Quality Management',
  },
  {
    label: 'ISO 9001',
    value: 'ISO 9001',
    color: '#6BCF7F',
    description: 'Quality Management',
  },
  {
    label: 'ISO 14001',
    value: 'ISO 14001',
    color: '#F5A962',
    description: 'Environmental Management',
  },
];

const CertificateUpload = () => {
  const { t } = useTranslation();
  const [selectedCertificate, setSelectedCertificate] = useState('');
  const [validityDate, setValidityDate] = useState('');
  const [uploadedCertificates, setUploadedCertificates] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const supplierId = user?.supplierId;

  useEffect(() => {
    if (!supplierId) {
      message.error(t('user_not_found'));
      return;
    }
    fetchCertificates();
  }, [supplierId]);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/Suppliers/${supplierId}`);
      const certificates = response.data.certificates || [];
      setUploadedCertificates(certificates);
      console.log('✅ Certificates loaded:', certificates.length);
    } catch (error) {
      const errorMessage = handleApiError(error, t('certificate_fetch_error'));
      message.error(errorMessage);
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCertificate = async (certificateId) => {
    try {
      const response = await apiClient.get(`/api/Certificates/${certificateId}/sas`);
      const sasUrl = response.data.sasUrl;
      if (sasUrl) {
        window.open(sasUrl, '_blank');
      } else {
        message.error(t('sas_url_error'));
      }
    } catch (error) {
      const errorMessage = handleApiError(error, t('certificate_view_error'));
      message.error(errorMessage);
      console.error('Error viewing certificate:', error);
    }
  };

  const handleCertificateChange = (value) => {
    setSelectedCertificate(value);
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    console.log('📅 Selected date (input):', selectedDate);
    setValidityDate(selectedDate);
  };

  const handleUploadChange = (info) => {
    const newFileList = [...info.fileList];
    setFileList(newFileList);
  };

  const isCertificateAlreadyUploaded = () => {
    return uploadedCertificates.some(
      (cert) => cert.certificateName === selectedCertificate
    );
  };

  const handleUpload = async () => {
    if (!selectedCertificate || !validityDate || fileList.length === 0) {
      message.error(t('complete_fields'));
      return;
    }

    if (isCertificateAlreadyUploaded()) {
      message.error(t('certificate_already_uploaded'));
      return;
    }

    setUploading(true);

    try {
      const formattedDate = `${validityDate}T00:00:00.000Z`;
      
      console.log('📅 Selected date:', validityDate);
      console.log('📅 Formatted for API:', formattedDate);

      const certificateData = {
        certificateName: selectedCertificate,
        certificateValidityDate: formattedDate,
        supplierId: supplierId,
        situation: '0',
      };

      console.log('📤 Sending certificate data:', certificateData);

      const certificateResponse = await apiClient.post('/api/Certificates', certificateData);
      const certificateId = certificateResponse.data.certificateId;

      console.log('✅ Certificate created:', certificateId);
      console.log('✅ Returned validity date:', certificateResponse.data.certificateValidityDate);

      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      formData.append('certificateId', certificateId);

      const uploadResponse = await apiClient.post('/api/Certificates/UploadFile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ File uploaded:', uploadResponse.data.fileUrl);

      message.success(t('certificate_uploaded_successfully'));
      setFileList([]);
      setSelectedCertificate('');
      setValidityDate('');
      
      fetchCertificates();
    } catch (error) {
      const errorMessage = handleApiError(error, t('upload_error'));
      message.error(errorMessage);
      console.error('Error uploading certificate:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (certificateId) => {
    try {
      await apiClient.delete(`/api/Certificates/${certificateId}/supplier-delete`);
      
      setUploadedCertificates(
        uploadedCertificates.filter((cert) => cert.certificateId !== certificateId)
      );
      message.success(t('certificate_deleted_successfully'));
      console.log('✅ Certificate deleted:', certificateId);
    } catch (error) {
      const errorMessage = handleApiError(error, t('certificate_delete_error'));
      message.error(errorMessage);
      console.error('Error deleting certificate:', error);
    }
  };

  // Statistics
  const totalCerts = uploadedCertificates.length;
  const approvedCerts = uploadedCertificates.filter(cert => cert.situation === '1').length;
  const pendingCerts = uploadedCertificates.filter(cert => cert.situation === '0').length;
  const expiringSoon = uploadedCertificates.filter(cert => {
    const daysUntilExpiry = moment(cert.certificateValidityDate).diff(moment(), 'days');
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  }).length;

  // ✅ Tablo kolonları
  const columns = [
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('certificate_name')}</span>,
      dataIndex: 'certificateName',
      key: 'certificateName',
      render: (text) => {
        const cert = certificateOptions.find((c) => c.value === text);
        return (
          <Space size={12}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: cert?.color || '#8c8c8c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SafetyCertificateOutlined style={{ color: 'white', fontSize: '18px' }} />
            </div>
            <div>
              <Text strong style={{ display: 'block', fontSize: '13px', color: '#262626' }}>
                {text}
              </Text>
              {cert && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {cert.description}
                </Text>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('validity_date')}</span>,
      dataIndex: 'certificateValidityDate',
      key: 'certificateValidityDate',
      width: 150,
      render: (text) => {
        const date = moment(text);
        const isExpired = date.isBefore(moment());
        const daysRemaining = date.diff(moment(), 'days');

        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <CalendarOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
              <Text style={{ fontSize: '13px', color: '#595959' }}>{date.format('DD MMM YYYY')}</Text>
            </Space>
            {isExpired ? (
              <Tag color="red" style={{ fontSize: '11px', margin: 0 }}>
                {t('expired')}
              </Tag>
            ) : daysRemaining <= 30 ? (
              <Tag color="orange" style={{ fontSize: '11px', margin: 0 }}>
                {daysRemaining} {t('days_remaining')}
              </Tag>
            ) : (
              <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>
                {t('valid')}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('status')}</span>,
      dataIndex: 'situation',
      key: 'situation',
      align: 'center',
      width: 140,
      render: (situation) => {
        if (situation === '0') {
          return (
            <Tag icon={<ClockCircleOutlined />} color="orange" style={{ fontSize: '12px' }}>
              {t('pending_approval')}
            </Tag>
          );
        } else if (situation === '1') {
          return (
            <Tag icon={<CheckCircleOutlined />} color="green" style={{ fontSize: '12px' }}>
              {t('approved')}
            </Tag>
          );
        } else {
          return (
            <Tag icon={<CloseCircleOutlined />} color="red" style={{ fontSize: '12px' }}>
              {t('rejected')}
            </Tag>
          );
        }
      },
    },
    {
      title: <span style={{ fontWeight: '600', fontSize: '13px' }}>{t('actions')}</span>,
      key: 'action',
      align: 'right',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewCertificate(record.certificateId)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
            <Popconfirm
              title={t('delete_confirmation')}
              description={t('delete_certificate_warning')}
              onConfirm={() => handleDelete(record.certificateId)}
              okText={t('yes')}
              cancelText={t('no')}
              okButtonProps={{ danger: true }}
            >
              <Tooltip title={t('delete')}>
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#262626' }}>
          {t('certificate_management')}
        </h2>
        <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
          {t('upload_and_manage_certificates')}
        </p>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('total_certificates')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#262626' }}>
                  {totalCerts}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: '#f6ffed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('approved')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#262626' }}>
                  {approvedCerts}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: '#fff7e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ClockCircleOutlined style={{ fontSize: '24px', color: '#faad14' }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('pending')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#262626' }}>
                  {pendingCerts}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: expiringSoon > 0 ? '#fff1f0' : '#f6ffed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CalendarOutlined style={{ 
                  fontSize: '24px', 
                  color: expiringSoon > 0 ? '#ff4d4f' : '#52c41a' 
                }} />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('expiring_soon')}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '600', 
                  color: expiringSoon > 0 ? '#ff4d4f' : '#262626',
                }}>
                  {expiringSoon}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Upload Card */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                <UploadOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                {t('upload_new_certificate')}
              </span>
            }
            bordered={false}
            style={{ borderRadius: '8px', height: '100%' }}
            bodyStyle={{ padding: '24px' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
              {/* Certificate Selection */}
              <div>
                <Text
                  strong
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    color: '#595959',
                  }}
                >
                  {t('select_certificate_type')}
                </Text>
                <Select
                  placeholder={t('select_certificate')}
                  style={{ width: '100%' }}
                  size="large"
                  onChange={handleCertificateChange}
                  value={selectedCertificate || undefined}
                >
                  {certificateOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: '4px',
                            backgroundColor: option.color,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ fontWeight: '500', fontSize: '13px' }}>{option.label}</div>
                          <div style={{ fontSize: '11px', color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {option.description}
                          </div>
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Validity Date */}
              <div>
                <Text
                  strong
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    color: '#595959',
                  }}
                >
                  {t('validity_date')}
                </Text>
                <Input
                  type="date"
                  size="large"
                  value={validityDate}
                  onChange={handleDateChange}
                  min={moment().format('YYYY-MM-DD')}
                  placeholder={t('select_expiry_date')}
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                  prefix={<CalendarOutlined style={{ color: '#8c8c8c' }} />}
                />
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* File Upload */}
              <div>
                <Text
                  strong
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    color: '#595959',
                  }}
                >
                  {t('upload_document')}
                </Text>
                <Dragger
                  fileList={fileList}
                  onChange={handleUploadChange}
                  beforeUpload={() => false}
                  maxCount={1}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  style={{
                    borderRadius: '8px',
                    border: '2px dashed #d9d9d9',
                    background: '#fafafa',
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <UploadOutlined style={{ fontSize: '40px', color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text" style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                    {t('click_or_drag_file')}
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    PDF, JPG, PNG, DOC, DOCX
                  </p>
                </Dragger>
              </div>

              {/* Upload Button */}
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                disabled={!selectedCertificate || !validityDate || fileList.length === 0}
                block
                size="large"
                icon={<UploadOutlined />}
                style={{
                  borderRadius: '8px',
                  height: '48px',
                  fontWeight: '600',
                  fontSize: '15px',
                  marginTop: '8px',
                  marginBottom: '16px', // ✅ Alt margin eklendi
                }}
              >
                {uploading ? t('uploading') : t('upload_certificate')}
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Table Card */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                  <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  {t('uploaded_certificates')}
                </span>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {uploadedCertificates.length} {t('certificates_in_total')}
                </Text>
              </div>
            }
            bordered={false}
            style={{ borderRadius: '8px', height: '100%' }}
            bodyStyle={{ padding: '16px' }}
          >
            <Table
              dataSource={uploadedCertificates}
              columns={columns}
              rowKey="certificateId"
              loading={loading}
              pagination={{
                pageSize: 8,
                showSizeChanger: false,
                showTotal: (total) => `${total} ${t('certificates')}`,
                style: { marginTop: '16px', marginBottom: 0 },
                size: 'small',
              }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div>
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          {t('no_certificates_uploaded')}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {t('upload_your_first_certificate')}
                        </Text>
                      </div>
                    }
                    style={{ padding: '40px 0' }}
                  />
                ),
              }}
            />
          </Card>
        </Col>
      </Row>

      <style jsx global>{`
        .ant-upload-drag:hover {
          border-color: #1890ff !important;
        }
        
        input[type="date"] {
          color-scheme: light;
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
          filter: opacity(0.6);
        }
        
        input[type="date"]::-webkit-calendar-picker-indicator:hover {
          filter: opacity(1);
        }
      `}</style>
    </div>
  );
};

export default CertificateUpload;
